import { TranslationsGetLanguageUseCase } from '@/core/domain/translation/usecases/translations-get-language.usecase';
import { TranslationsLoaderUseCase } from '@/core/domain/translation/usecases/translations-loader.usecase';
import {
    AfterViewInit,
    Component,
    HostListener,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Settings } from 'luxon';
import { environment } from '@/env/environment';
import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';
import { SsrService } from '@/common/services/ssr/ssr.service';
import { AlertService } from '@/common/services/alert/alert.service';
import { AlertComponent } from './shared/components/alert/alert.component';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { CdnService } from '@/common/services/cdn/cdn.service';
import _ from 'lodash';
import { NavigationEnd, Router } from '@angular/router';
import { UrlService } from '@/common/services/url/url.service';
import { ConfigurationService } from '@/common/services/configuration/configuration.service';
import { LEGACY_TYPE_FLOW_KEY } from '@/common/utils/flow/type-flow.util';
import { filter } from 'rxjs';
import { CookieService } from 'ngx-cookie';
import { Location } from '@angular/common';
import { CanonicalService } from '@/core/seo/canonical.service';
import { TranslateService } from '@ngx-translate/core';
import { Meta } from '@angular/platform-browser';

@Component({
    selector: 'fe-alamo-main-site',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit {
    public debugBreakpoints = false;

    public maintenance = false;
    public whatsappUrl = '';
    @ViewChild('coreAlert') coreAlert!: AlertComponent;

    constructor(
        private readonly translationsLoader: TranslationsLoaderUseCase,
        private readonly getLanguageUseCase: TranslationsGetLanguageUseCase,
        public breakpoint: BreakpointService,
        private readonly ssr: SsrService,
        private readonly alert: AlertService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly router: Router,
        private readonly url: UrlService,
        private readonly configuration: ConfigurationService,
        private readonly cookieService: CookieService,
        private location: Location,
        private meta: Meta,
        private translate: TranslateService,
        private readonly canonical: CanonicalService,
    ) {
        this.initDatePicker();
        this.debugBreakpoints = environment.debugBreakpoints;
    }

    ngAfterViewInit(): void {
        this.alert.coreAlertTemplate = this.coreAlert;
    }

    ngOnInit(): void {
        this.clearLegacyTypeFlow();
        this.initTranslations();
        this.updateBreakpoint();
        this.initGtmDom();
        this.initFeatureFlags();
        this.handleRouteChange();
        this.canonical.init();
    }

    @HostListener('window:resize', ['$event'])
    onResize(): void {
        this.updateBreakpoint();
    }

    private initFeatureFlags(): void {
        this.configuration.featuresFlags
            .pipe(
                filter((item) => {
                    return (
                        item?.maintenance === true &&
                        !_.includes(
                            this.location.path(),
                            'site-under-maintenance',
                        ) &&
                        !_.includes(
                            this.location.path(),
                            'sitio-en-mantenimiento',
                        ) &&
                        this.cookieService.get('debug') !== '1'
                    );
                }),
            )
            .subscribe(() => {
                this.maintenance = true;
            });
        this.configuration.reloadFeatureFlags();
    }

    private clearLegacyTypeFlow(): void {
        if (this.ssr.isPlatformServer()) {
            return;
        }
        localStorage.removeItem(LEGACY_TYPE_FLOW_KEY);
    }

    private initDatePicker(): void {
        if (this.ssr.isPlatformServer()) {
            return;
        }
        const js = [
            CdnService.url('js/easypick/core@1.2.1.js'),
            CdnService.url('js/easypick/base-plugin@1.2.1.js'),
            CdnService.url(
                'js/easypick/plugins/ChangeDayContainerPlugin/index.umd.js',
            ),
        ];
        _.forEach(js, (script) => {
            const scriptElement = document.createElement('script');
            scriptElement.src = script;
            scriptElement.async = false;
            document.head.prepend(scriptElement);
        });
    }

    private initGtmDom(): void {
        if (this.ssr.isPlatformServer()) {
            return;
        }
        this.gtmService.addGtmToDom();
    }

    private updateBreakpoint(): void {
        if (this.ssr.isPlatformServer()) {
            return;
        }
        const width = window.innerWidth;
        if (width < 576) {
            this.breakpoint.current = 'xs';
        } else if (width < 768) {
            this.breakpoint.current = 'sm';
        } else if (width < 1024) {
            this.breakpoint.current = 'md';
        } else if (width < 1060) {
            this.breakpoint.current = 'lmd';
        } else if (width < 1357) {
            this.breakpoint.current = 'lg';
        } else if (width < 1527) {
            this.breakpoint.current = 'xl';
        } else {
            this.breakpoint.current = 'xxl';
        }
    }

    private initTranslations(): void {
        const currentLanguage = this.getLanguageUseCase.execute();
        this.initLuxon(currentLanguage.language);
        this.translationsLoader
            .execute({
                lang: currentLanguage.language,
                reload: true,
            })
            .subscribe();
    }

    private handleRouteChange(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.whatsappUrl = this.translate.instant(
                    event.url === '/es/empresas' || event.url === '/en/empresas'
                        ? 'fe_core_footer_whatsapp_enterprise'
                        : 'fe_core_footer_whatsapp',
                );
            }
        });
    }

    private initLuxon(locale: string): void {
        Settings.defaultLocale = locale;
    }
}
